import _ from 'lodash';
import moment from 'moment';

const CHAR_LIMIT_DEFAULT = 500;

/**
 * Format name for url
 * Replaces whitespace with hyphens, removes all but letters and hyphens
 * @param {String} name a name on a url
 * @return {String} name formatted for url
 */
export function formatNameForUrl(name) {
  return (name || '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z-]/g, '')
    .toLowerCase();
}

/**
 * Format an external url for use in a link
 * prepends with // if no protocol is on the url
 * @param {String} url Input url
 * @return {String} Formatted url
 */
export function formatExternalLink(url) {
  if (!url) {
    return null;
  }
  const lowercaseUrl = url.toLowerCase();

  if (
    lowercaseUrl.startsWith('http://') ||
    lowercaseUrl.startsWith('https://') ||
    lowercaseUrl.startsWith('//')
  ) {
    return url;
  }
  return `//${url}`;
}

/**
 * Formats a phone number to a 10 digit format.
 * @param  {String} phoneNumber Input number.
 * @return {String} Formatted number as per (XXX) YYY-ZZZZ.
 */
export function formatPhoneNumber(phoneNumber) {
  let formattedNumber = (phoneNumber || '').replace(/[^A-Z0-9]/g, '');

  if (
    !formattedNumber ||
    formattedNumber.length < 10 ||
    formattedNumber.length > 12
  ) {
    return null;
  }
  if (formattedNumber.length === 11) {
    if (formattedNumber[0] === '1') {
      formattedNumber = formattedNumber.substr(1);
    } else {
      return null;
    }
  }
  return `(${formattedNumber.substr(0, 3)}) ${formattedNumber.substr(
    3,
    3
  )}-${formattedNumber.substr(6, 4)}`;
}

/**
 * Formats a phrase to capitalize first letters.
 * @param  {String} phrase Input string.
 * @param  {String} wordSeparator The regex to determine how to break out the words
 * @return {String} Formatted string.
 */
export function formatPhrase(phrase, wordSeparator = /[^ ]+/g) {
  let formattedPhrase = '';

  if (phrase) {
    formattedPhrase = _.words(phrase, wordSeparator)
      .map(word => {
        let processedWords = word;
        const hyphenated = _.words(processedWords, /[^-]+/g);

        if (_.size(hyphenated) > 1) {
          processedWords = hyphenated
            .map(hyphenedWord => _.capitalize(hyphenedWord))
            .join('-');
        } else {
          processedWords = _.capitalize(processedWords);
        }

        return processedWords;
      })
      .join(' ');
  }

  return formattedPhrase;
}

/**
 * Formats Addresses
 * @param  {Object} addressObj Address object
 * @return {String} Formats address in 'address, city, state, country' using full display names.
 */
export function formatAddress(addressObj) {
  let formattedAddress = '';

  if (_.isObject(addressObj)) {
    const {address, address1, address2, city, state, country} = addressObj;
    const addressDetails = [address, address1, address2, city, state, country];
    const serviceAddress = addressDetails.filter(section => section);

    formattedAddress = serviceAddress.join(', ');
  }

  return formatPhrase(formattedAddress);
}

/**
 * Configure the condolence's date
 * @param {String} date the unformated date
 * @param {String} prevFormat The previous date format for proper parsing
 * @param {String} nextFormat The format you want outputted by moment
 * @returns {String} Returns a formated date string (e.g. June, 23, 2016)
 */
export function formatDate(date, prevFormat, nextFormat) {
  let retVal = null;
  const validNextFormat = _.isString(nextFormat) ? nextFormat : null;

  if (date) {
    retVal = _.isString(prevFormat)
      ? moment.utc(date, prevFormat).format(validNextFormat)
      : date;
  }

  return retVal;
}

/**
 * Formats the page title
 * @param  {String} firstName The first name
 * @param  {String} lastName The last name
 * @param  {String} city The city
 * @param  {String} state The state
 * @return {String} The formatted title
 */
export function formatTitle(firstName, lastName, city, state) {
  const displayFirstName = firstName ? `${firstName} ` : '';
  const displayLastName = lastName ? `${lastName} ` : '';
  const displayCity = city ? `${city}${state ? ', ' : ' '}` : '';
  const displayState = state ? `${state} ` : '';
  const divider = city || state ? '| ' : '';

  return `${displayFirstName}${displayLastName}Obituary - ${displayCity}${displayState}${divider}Legacy.com`;
}

/**
 * Truncate a message
 * @param {String} message the condolence's message
 * @param {Number} length The character limit for the message
 * @param {String} separator character to truncate to
 * @returns {String} Formatted Message
 */
export function truncateMessage(
  message,
  length = CHAR_LIMIT_DEFAULT,
  separator = ' '
) {
  const messageLength = _.get(message, 'length');
  let retMessage = '';

  if (messageLength) {
    retMessage =
      messageLength > length
        ? _.truncate(message, {length, separator})
        : message;
  }

  return retMessage;
}

/**
 * returns formatted name
 * @param  {Object} nameObj name object from person-service
 * @return {String}         formatted name
 */
export function getName(nameObj) {
  return _.trim(
    _.reduce(
      _.pick(nameObj, ['first', 'last']),
      (acc, val) => {
        if (!_.isNil(val)) {
          acc.push(val);
        }
        return acc;
      },
      []
    ).join(' ')
  );
}

/**
 * returns a rough equivalent to the simple_format helper used with Rails
 * the purpose of the function is to take text and format with <p> and <br />
 * tags based on the presence of line breaks
 * @param  {Sting}   text string to format
 * @return {String}         formatted string
 */
export function simpleFormat(text) {
  let formatted = text;

  if (formatted) {
    formatted = _.trim(formatted);
    formatted = formatted.replace(/\r\n?/g, '\n');
    if (formatted.length > 0) {
      const formattedWithP = formatted.replace(/\n\n+/g, '</p><p>');

      if (formattedWithP !== formatted) {
        formatted = `<p>${formattedWithP}</p>`;
      }
      formatted = formatted.replace(/\n/g, '<br />');
    }
  }

  return formatted;
}

/**
 * returns sanitized text and remove all html tags
 * @param  {text} text to be sanitized and remove all html tags
 * @param  {Array} tags list of tags to allow in html
 * @return {String} text without html
 */
export function sanitizeText (text, tags = []) {
  if (text) {
    // strip all script and object content first from text
    const scriptRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
    const objectRegex = /<object\b[^<]*(?:(?!<\/object)<[^<]*)*<\/object>/gi;
    // remove attributes of <p> tag
    const pTagRegex = /<p\s.*?>/gi;
    const returnText = text.replace(scriptRegex, '').replace(objectRegex, '').replace(pTagRegex, '<p>');
    const hasTags = tags.length > 0;
    let tagsRegex = "";

    if (hasTags) {
      const tagsInRegEx = [];

      tags.forEach(tag => {
        tagsInRegEx.push(`${tag}|\/${tag}`);
      });
      tagsRegex = new RegExp(`<(?!(${tagsInRegEx.join('|')})(\\s|>)).*?>`, 'gi');
    }
    const regex = (tags.length > 0) ? tagsRegex : /<[^>]*>/gi;

    return returnText.replace(regex, '');
  }
  return text;
}



// WEBPACK FOOTER //
// ./src/appModules/utilities/formatHelpers.js